import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, Typography, Button, TextField, IconButton,
  Checkbox, FormControlLabel, Alert, CircularProgress,
  Skeleton, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, Table, TableHead, TableBody,
  TableRow, TableCell
} from '@mui/material';
import { Add, Search, Print, Close, CreditScore } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import api from '../api/authApi';

const todayStr = () => new Date().toISOString().split('T')[0];
const fmt2 = (v) => parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Advance() {
  const { theme } = useApp();
  const { user }  = useAuth();

  const [rows,        setRows]        = useState([]);
  const [totalCount,  setTotalCount]  = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [includePaid, setIncludePaid] = useState(false);
  const [search,      setSearch]      = useState('');
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(20);
  const [showAdd,     setShowAdd]     = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/advance', { params: { includePaid, search, page, pageSize } });
      if (res.data?.success) {
        setRows(res.data.data?.data || []);
        setTotalCount(res.data.data?.totalCount ?? 0);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [includePaid, search, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalAmt  = rows.reduce((s, r) => s + (parseFloat(r.amount)  || 0), 0);
  const totalPaid = rows.reduce((s, r) => s + (parseFloat(r.paid)    || 0), 0);
  const totalBal  = rows.reduce((s, r) => s + (parseFloat(r.balance) || 0), 0);

  const handlePrint = () => {
    const html = `<html><head><style>
      body{font-family:Arial,sans-serif;font-size:12px;margin:20px}
      h2{margin:0;font-size:15px}h4{margin:2px 0 10px;font-size:12px;color:#555}
      .hdr{display:flex;justify-content:space-between;margin-bottom:8px}
      .title{color:#1a4fa0;font-size:13px;font-weight:bold}
      table{width:100%;border-collapse:collapse;margin-top:8px}
      th{background:#e8f0fe;padding:6px 8px;text-align:left;border:1px solid #ccc;font-size:11px}
      td{padding:5px 8px;border:1px solid #ddd;font-size:11px}
      .r{text-align:right} .tot td{font-weight:bold;background:#f5f5f5} .red{color:#c00}
    </style></head><body>
      <div class="hdr">
        <div><h2>${user?.centerName||''}</h2><h4>${user?.centerCode||''}</h4></div>
        <div class="title">Advance Register</div>
      </div>
      <table><thead><tr><th>Code</th><th>Name</th><th class="r">Amount</th><th class="r">Balance</th></tr></thead>
      <tbody>
        ${rows.map(r=>`<tr><td>${r.code}</td><td>${r.name}</td>
          <td class="r">${parseFloat(r.amount).toFixed(2)}</td>
          <td class="r red">${parseFloat(r.balance).toFixed(2)}</td></tr>`).join('')}
        <tr class="tot"><td colspan="2">Total</td>
          <td class="r">${totalAmt.toFixed(2)}</td>
          <td class="r">${totalBal.toFixed(2)}</td></tr>
      </tbody></table></body></html>`;
    const w = window.open('','_blank','width=800,height=600');
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => w.print(), 400);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const from = totalCount === 0 ? 0 : (page-1)*pageSize+1;
  const to   = Math.min(page*pageSize, totalCount);

  const thStyle = {
    fontSize: '0.82rem', fontWeight: 700, color: '#374151',
    padding: '10px 14px', borderBottom: '1px solid #e5e7eb',
    background: '#fff', whiteSpace: 'nowrap', cursor: 'pointer',
  };
  const tdStyle = (bold, align, color) => ({
    fontSize: '0.82rem', padding: '9px 14px',
    borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap',
    fontWeight: bold ? 700 : 400, textAlign: align || 'left',
    color: color || '#111827',
  });

  return (
    <Layout title="Advance">
      {error   && <Alert severity="error"   sx={{ mb:2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb:2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Top bar */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <CreditScore sx={{ color:theme.primary }}/>
          <Typography variant="h6" sx={{ fontWeight:800 }}>Advance</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add/>} onClick={() => setShowAdd(true)}
          sx={{ borderRadius:2, fontWeight:700, textTransform:'none',
            background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          + Add New Advance
        </Button>
      </Box>

      {/* Include Paid */}
      <FormControlLabel sx={{ mb:1 }}
        control={<Checkbox size="small" checked={includePaid} onChange={e => setIncludePaid(e.target.checked)}
          sx={{ color:theme.primary, '&.Mui-checked':{ color:theme.primary } }}/>}
        label={<Typography sx={{ fontSize:'0.85rem', fontWeight:600 }}>Include Paid</Typography>}/>

      {/* Table card */}
      <Card sx={{ borderRadius:2, boxShadow:'0 1px 6px rgba(0,0,0,0.08)', overflow:'hidden' }}>

        {/* Table toolbar */}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          px:2, py:1, background:theme.light, borderBottom:`1px solid ${theme.border}` }}>
          <Typography sx={{ fontWeight:700, color:theme.primary, fontSize:'0.88rem' }}>Advance</Typography>
          <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
            <Button size="small" startIcon={<Print sx={{ fontSize:15 }}/>} onClick={handlePrint}
              variant="outlined"
              sx={{ borderRadius:1.5, textTransform:'none', fontSize:'0.78rem',
                borderColor:theme.primary, color:theme.primary, height:32,
                '&:hover':{ background:theme.light } }}>
              PRINT
            </Button>
            <TextField size="small" placeholder="Search"
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              InputProps={{ startAdornment:<Search sx={{ fontSize:16, color:'#9ca3af', mr:0.5 }}/> }}
              sx={{ width:180, '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem', height:32 } }}/>
          </Box>
        </Box>

        {/* Table */}
        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'2px solid #e5e7eb' }}>
                <th style={{ ...thStyle, width:60  }}>SrNo</th>
                <th style={{ ...thStyle, width:100 }}>Code ↕</th>
                <th style={{ ...thStyle }}>Name ↕</th>
                <th style={{ ...thStyle, textAlign:'right' }}>Amount ↕</th>
                <th style={{ ...thStyle, textAlign:'right' }}>Paid ↕</th>
                <th style={{ ...thStyle, textAlign:'right' }}>Balance ↕</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({length:5}).map((_,i) => (
                    <tr key={i}>{Array.from({length:6}).map((__,j) => (
                      <td key={j} style={{ padding:'10px 14px' }}><Skeleton height={16}/></td>
                    ))}</tr>
                  ))
                : rows.length === 0
                  ? <tr><td colSpan={6} style={{ textAlign:'center', padding:'48px', color:'#9ca3af', fontSize:'0.85rem' }}>
                      No Records Found.
                    </td></tr>
                  : <>
                      {rows.map((row, idx) => {
                        const isPaid = parseFloat(row.balance) <= 0;
                        return (
                          <tr key={idx} style={{ background: idx%2===0?'#fff':'#fafafa' }}>
                            <td style={tdStyle(false,'left','#6b7280')}>{row.srNo}</td>
                            <td style={tdStyle(true)}>{row.code}</td>
                            <td style={tdStyle(false)}>{row.name}</td>
                            <td style={tdStyle(false,'right')}>{fmt2(row.amount)}</td>
                            <td style={tdStyle(false,'right')}>{fmt2(row.paid)}</td>
                            <td style={tdStyle(true,'right', isPaid?'#16a34a':'#dc2626')}>
                              {isPaid ? 'PAID' : fmt2(row.balance)}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Totals row */}
                      <tr style={{ background:'#f9fafb', borderTop:'2px solid #e5e7eb' }}>
                        <td style={tdStyle(false,'left','#6b7280')}>-</td>
                        <td style={tdStyle(false)}></td>
                        <td style={tdStyle(true)}>Total</td>
                        <td style={tdStyle(true,'right')}>{fmt2(totalAmt)}</td>
                        <td style={tdStyle(true,'right')}>{fmt2(totalPaid)}</td>
                        <td style={tdStyle(true,'right')}>{fmt2(totalBal)}</td>
                      </tr>
                    </>
              }
            </tbody>
          </table>
        </Box>

        {/* Pagination */}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center',
          gap:0.5, px:2, py:1.5, borderTop:'1px solid #f0f0f0', background:'#fafafa' }}>
          <Typography sx={{ fontSize:'0.78rem', color:'#6b7280', mr:2 }}>
            Showing {from} to {to} of {totalCount} entries
          </Typography>
          {[
            { lbl:'«', target:1,          disabled:page<=1 },
            { lbl:'‹', target:page-1,     disabled:page<=1 },
            { lbl:'›', target:page+1,     disabled:page>=totalPages },
            { lbl:'»', target:totalPages, disabled:page>=totalPages },
          ].map(({ lbl, target, disabled }) => (
            <IconButton key={lbl} size="small" disabled={disabled} onClick={() => setPage(target)}
              sx={{ width:28, height:28, border:'1px solid #e5e7eb', borderRadius:1,
                '&:disabled':{ color:'#d1d5db' } }}>
              <Typography sx={{ fontSize:12, fontWeight:700 }}>{lbl}</Typography>
            </IconButton>
          ))}
          <Box sx={{ mx:1, px:2, py:0.4, background:theme.primary, color:'#fff',
            borderRadius:1, fontSize:'0.78rem', fontWeight:700, minWidth:28, textAlign:'center' }}>
            {page}
          </Box>
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft:4, fontSize:'0.78rem', padding:'3px 6px',
              borderRadius:4, border:'1px solid #d1d5db', background:'#fff' }}>
            {[10,20,50,100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </Box>
      </Card>

      {showAdd && (
        <AddAdvanceDialog open={showAdd} onClose={() => setShowAdd(false)}
          onSaved={() => {
            fetchData(); setShowAdd(false);
            setSuccess('Advance added successfully.');
            setTimeout(() => setSuccess(''), 3000);
          }}
        />
      )}
    </Layout>
  );
}

// ── Add Advance Dialog ────────────────────────────────────
function AddAdvanceDialog({ open, onClose, onSaved }) {
  const { theme } = useApp();

  const [farmerCode,   setFarmerCode]   = useState('');
  const [farmerName,   setFarmerName]   = useState('');
  const [date,         setDate]         = useState(todayStr());
  const [amount,       setAmount]       = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [comments,     setComments]     = useState('');
  const [saveAndCont,  setSaveAndCont]  = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [searching,    setSearching]    = useState(false);
  const [error,        setError]        = useState('');

  const codeRef = useRef('');
  const foundRef = useRef(false);
  useEffect(() => { codeRef.current = farmerCode; }, [farmerCode]);

  const lookupFarmer = useCallback(async (code) => {
    if (!code) return;
    setSearching(true); setError('');
    try {
      const res = await api.get(`/collection/farmer/${code}`);
      if (res.data?.success && res.data?.data) {
        setFarmerName(res.data.data.farmerName);
        foundRef.current = true;
      } else {
        setFarmerName(''); foundRef.current = false;
        setError('Farmer not found.');
      }
    } catch { setError('Farmer lookup failed.'); }
    finally { setSearching(false); }
  }, []);

  const handleReset = () => {
    setFarmerCode(''); setFarmerName(''); setAmount('');
    setInterestRate(''); setComments(''); setError('');
    foundRef.current = false;
  };

  const handleSave = async () => {
    if (!farmerCode) { setError('Enter farmer code.'); return; }
    if (!farmerName) { setError('Farmer not found. Press search.'); return; }
    if (!amount)     { setError('Enter amount.'); return; }
    setSaving(true);
    try {
      const res = await api.post('/advance', {
        farmerCode, date,
        amount:       parseFloat(amount),
        interestRate: interestRate ? parseFloat(interestRate) : null,
        comments:     comments || null,
      });
      if (res.data?.success) {
        if (saveAndCont) handleReset();
        else onSaved();
      } else { setError(res.data?.message || 'Save failed.'); }
    } catch (e) { setError(e.response?.data?.message || e.message || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const lbSx = { fontSize:'0.82rem', fontWeight:600, color:'#374151', mb:0.5 };
  const inSx = { '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem' } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx:{ borderRadius:2.5 } }}>
      <DialogTitle sx={{ py:1.5, px:2.5, fontWeight:700, fontSize:'0.95rem',
        background:`linear-gradient(135deg,${theme.primary},${theme.hover})`, color:'#fff',
        display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        Add New Advance
        <IconButton size="small" onClick={onClose} sx={{ color:'#fff' }}>
          <Close sx={{ fontSize:18 }}/>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px:2.5, pt:2.5, pb:1 }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb:1.5, py:0.5 }}>{error}</Alert>}

        {/* Farmer Code */}
        <Box sx={{ mb:1.8 }}>
          <Typography sx={lbSx}>Farmer Code <span style={{ color:'#dc2626' }}>*</span></Typography>
          <Box sx={{ display:'flex', gap:1 }}>
            <TextField size="small" fullWidth placeholder="Enter code"
              value={farmerCode}
              onChange={e => { setFarmerCode(e.target.value.toUpperCase()); setFarmerName(''); foundRef.current=false; }}
              onKeyDown={e => { if(e.key==='Enter'){e.preventDefault(); lookupFarmer(codeRef.current.trim()); }}}
              onBlur={() => { if(codeRef.current.trim() && !foundRef.current) lookupFarmer(codeRef.current.trim()); }}
              sx={inSx}/>
            <IconButton onClick={() => lookupFarmer(codeRef.current.trim())} disabled={searching}
              sx={{ border:`1.5px solid ${theme.border}`, borderRadius:1.5, color:theme.primary }}>
              {searching ? <CircularProgress size={16}/> : <Search sx={{ fontSize:18 }}/>}
            </IconButton>
          </Box>
          {farmerName && (
            <Typography sx={{ fontSize:'0.75rem', color:theme.primary, mt:0.3, fontWeight:600 }}>
              ✅ {farmerName}
            </Typography>
          )}
        </Box>

        {/* Date */}
        <Box sx={{ mb:1.8 }}>
          <Typography sx={lbSx}>Date <span style={{ color:'#dc2626' }}>*</span></Typography>
          <TextField type="date" size="small" fullWidth value={date}
            onChange={e => setDate(e.target.value)}
            inputProps={{ max:todayStr() }} sx={inSx}/>
        </Box>

        {/* Amount */}
        <Box sx={{ mb:1.8 }}>
          <Typography sx={lbSx}>Amount <span style={{ color:'#dc2626' }}>*</span></Typography>
          <TextField type="number" size="small" fullWidth value={amount}
            onChange={e => setAmount(e.target.value)} sx={inSx}/>
        </Box>

        {/* Interest Rate */}
        <Box sx={{ mb:0.5 }}>
          <Typography sx={lbSx}>Interest Rate</Typography>
          <TextField type="number" size="small" fullWidth value={interestRate}
            onChange={e => setInterestRate(e.target.value)} sx={inSx}/>
        </Box>
        <Typography sx={{ fontSize:'0.74rem', color:theme.primary, mb:1.8, ml:0.3 }}>
          Interest per Month
        </Typography>

        {/* Comments */}
        <Box sx={{ mb:1.5 }}>
          <Typography sx={lbSx}>Comments <span style={{ color:'#dc2626' }}>*</span></Typography>
          <TextField size="small" fullWidth multiline rows={2} value={comments}
            onChange={e => setComments(e.target.value)} sx={inSx}/>
        </Box>

        <FormControlLabel
          control={<Checkbox size="small" checked={saveAndCont} onChange={e => setSaveAndCont(e.target.checked)}
            sx={{ color:theme.primary, '&.Mui-checked':{ color:theme.primary } }}/>}
          label={<Typography sx={{ fontSize:'0.82rem' }}>Save and continue</Typography>}/>
      </DialogContent>

      <DialogActions sx={{ px:2.5, pb:2, gap:1 }}>
        <Button variant="outlined" onClick={handleReset}
          sx={{ borderRadius:1.5, textTransform:'none', borderColor:'#d1d5db', color:'#374151' }}>
          Reset
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ borderRadius:1.5, textTransform:'none',
            background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          {saving ? <CircularProgress size={16} sx={{ color:'#fff' }}/> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}